import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../App';
import Navigation from './Navigation';
import Footer from './Footer';
import InvoiceGeneratorFixed from './InvoiceGeneratorFixed';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ShoppingCart, 
  Plus, 
  Clock, 
  CheckCircle,
  Trash2,
  Cookie,
  Coffee,
  Cake,
  DollarSign,
  Receipt
} from 'lucide-react';

const SalesEntry = () => {
  const { user, API } = useContext(UserContext);
  const [allProducts, setAllProducts] = useState([]);
  const [selectedCounter, setSelectedCounter] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [quantity, setQuantity] = useState('');
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [recentEntries, setRecentEntries] = useState([]);
  const [showInvoiceGenerator, setShowInvoiceGenerator] = useState(false);
  const [lastSaleData, setLastSaleData] = useState(null);

  const counters = [
    { id: 'pain', name: 'Pains', color: 'bg-yellow-100 text-yellow-800' },
    { id: 'viennoiserie', name: 'Viennoiserie', color: 'bg-purple-100 text-purple-800' },
    { id: 'gateaux', name: 'Gâteaux', color: 'bg-pink-100 text-pink-800' }
  ];

  useEffect(() => {
    let mounted = true;
    
    const loadData = async () => {
      try {
        setPageLoading(true);
        
        const [productsRes, entriesRes] = await Promise.all([
          axios.get(`${API}/products`).catch(() => ({ data: [] })),
          axios.get(`${API}/sales?date=${new Date().toISOString().split('T')[0]}`).catch(() => ({ data: [] }))
        ]);
        
        if (mounted) {
          setAllProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
          setRecentEntries(Array.isArray(entriesRes.data) ? entriesRes.data.slice(0, 10) : []);
        }
      } catch (error) {
        console.error('Error loading sales data:', error);
        if (mounted) {
          toast.error('Erreur lors du chargement');
          setAllProducts([]);
          setRecentEntries([]);
        }
      } finally {
        if (mounted) {
          setPageLoading(false);
        }
      }
    };
    
    loadData();
    
    return () => {
      mounted = false;
    };
  }, [API]);

  const getFilteredProducts = () => {
    if (!selectedCounter || !Array.isArray(allProducts)) return [];
    return allProducts.filter(product => product && product.counter === selectedCounter);
  };

  const getProductDetails = (productId) => {
    if (!productId || !Array.isArray(allProducts)) return null;
    return allProducts.find(p => p && p.id === productId) || null;
  };

  const handleCounterSelect = (counterId) => {
    try {
      console.log('Selecting sales counter:', counterId);
      setSelectedCounter(counterId);
      setSelectedProduct('');
      setQuantity('');
    } catch (error) {
      console.error('Error selecting counter:', error);
      toast.error('Erreur lors de la sélection du guichet');
    }
  };

  const handleProductChange = (event) => {
    try {
      const productId = event.target.value;
      console.log('Sales product selection:', productId);
      
      if (!productId) {
        setSelectedProduct('');
        setQuantity('');
        return;
      }
      
      const product = getProductDetails(productId);
      console.log('Found sales product:', product);
      
      if (product) {
        setSelectedProduct(productId);
        console.log('Sales product selection successful:', product.name);
      } else {
        console.error('Sales product not found:', productId);
        setSelectedProduct('');
        toast.error('Produit introuvable');
      }
    } catch (error) {
      console.error('Critical error in sales product selection:', error);
      setSelectedProduct('');
      setQuantity('');
      toast.error('Erreur lors de la sélection du produit');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (!selectedProduct || !quantity || parseFloat(quantity) <= 0) {
        toast.error('Veuillez remplir tous les champs correctement');
        return;
      }

      setLoading(true);

      const productSold = getProductDetails(selectedProduct);
      
      await axios.post(`${API}/sales`, {
        product_id: selectedProduct,
        quantity_sold: parseFloat(quantity),
        user_id: user.id
      });

      toast.success('Vente enregistrée avec succès !');
      
      // Préparer les données pour la facture
      const saleData = {
        products: [{
          id: selectedProduct,
          name: productSold?.name || 'Produit',
          price: productSold?.price || 0,
          quantity: parseFloat(quantity)
        }]
      };
      
      setLastSaleData(saleData);
      
      setSelectedProduct('');
      setQuantity('');
      
      const entriesRes = await axios.get(`${API}/sales?date=${new Date().toISOString().split('T')[0]}`);
      if (Array.isArray(entriesRes.data)) {
        setRecentEntries(entriesRes.data.slice(0, 10));
      }
      
      // Proposer la génération de reçu
      toast.info('💡 Souhaitez-vous générer un reçu pour cette vente ?', {
        duration: 8000,
        action: {
          label: '📄 Générer Reçu',
          onClick: () => setShowInvoiceGenerator(true)
        }
      });
      
    } catch (error) {
      console.error('Error creating sales record:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (entryId) => {
    try {
      if (!entryId) return;
      
      if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette vente ?')) {
        return;
      }

      await axios.delete(`${API}/sales/${entryId}?user_role=${user.role}`);
      toast.success('Vente supprimée !');
      
      const entriesRes = await axios.get(`${API}/sales?date=${new Date().toISOString().split('T')[0]}`);
      if (Array.isArray(entriesRes.data)) {
        setRecentEntries(entriesRes.data.slice(0, 10));
      }
    } catch (error) {
      console.error('Error deleting sales record:', error);
      if (error.response?.status === 403) {
        toast.error('Accès refusé');
      } else {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const canDelete = () => {
    return user && (user.role === 'owner' || user.role === 'admin');
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'Heure inconnue';
    try {
      return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Heure invalide';
    }
  };

  const calculateRevenue = () => {
    try {
      if (selectedProduct && quantity) {
        const product = getProductDetails(selectedProduct);
        return product ? product.price * parseInt(quantity) : 0;
      }
      return 0;
    } catch (error) {
      return 0;
    }
  };

  const getCounterIcon = (counterId) => {
    switch (counterId) {
      case 'pain':
        return <img src="https://customer-assets.emergentagent.com/job_baker-point-of-sale/artifacts/32b5mga4_image.png" alt="Pains" className="h-4 w-4 object-cover rounded" />;
      case 'viennoiserie':
        return <img src="https://customer-assets.emergentagent.com/job_baker-point-of-sale/artifacts/d7eif7ef_image.png" alt="Viennoiseries" className="h-4 w-4 object-cover rounded" />;
      case 'gateaux':
        return <Cake className="h-4 w-4" />;
      default:
        return <ShoppingCart className="h-4 w-4" />;
    }
  };

  if (pageLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement des ventes...</p>
          </div>
        </div>
      </div>
    );
  }

  const filteredProducts = getFilteredProducts();

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
      <Navigation />
      
      <div className="content-wrapper px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-width-container flex items-center justify-between mb-8 fade-in">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Saisie des Ventes
            </h1>
            <p className="text-gray-600">
              Enregistrez les ventes par guichet en temps réel
            </p>
          </div>
        </div>

        <div className="max-width-cards grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Sales Form */}
          <Card className="bg-white/70 glass border-0 shadow-lg slide-up">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-green-600" />
                Nouvelle Vente
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Counter Selection */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Guichet
                  </Label>
                  <div className="grid grid-cols-3 gap-3">
                    {counters.map((counter) => (
                      <button
                        key={counter.id}
                        type="button"
                        onClick={() => handleCounterSelect(counter.id)}
                        className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                          selectedCounter === counter.id
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-green-300'
                        }`}
                        data-testid={`counter-${counter.id}`}
                      >
                        {counter.id === 'pain' && (
                          <img src="https://customer-assets.emergentagent.com/job_baker-point-of-sale/artifacts/32b5mga4_image.png" alt="Pains" className="h-8 w-8 mx-auto mb-2 object-cover rounded" />
                        )}
                        {counter.id === 'viennoiserie' && (
                          <img src="https://customer-assets.emergentagent.com/job_baker-point-of-sale/artifacts/d7eif7ef_image.png" alt="Viennoiseries" className="h-8 w-8 mx-auto mb-2 object-cover rounded" />
                        )}
                        {counter.id === 'gateaux' && (
                          <Cake className="h-8 w-8 mx-auto mb-2 text-gray-600" />
                        )}
                        <p className="text-sm font-medium">{counter.name}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Product Selection */}
                {selectedCounter && filteredProducts.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">
                      Produit ({filteredProducts.length} disponible(s))
                    </Label>
                    
                    <select
                      value={selectedProduct}
                      onChange={handleProductChange}
                      className="w-full h-12 px-4 text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white hover:border-green-400 transition-colors"
                      data-testid="product-select-safe"
                    >
                      <option value="" disabled>Sélectionnez un produit</option>
                      {filteredProducts.map((product) => (
                        <option key={`sales-product-${product.id}`} value={product.id}>
                          {product.name} - {product.price} F CFA
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Quantity Input */}
                {selectedProduct && (
                  <div className="space-y-2">
                    <Label htmlFor="quantity" className="text-sm font-medium text-gray-700">
                      Quantité vendue
                    </Label>
                    <Input
                      id="quantity"
                      type="number"
                      step="0.1"
                      min="0.1"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      className="h-12 text-base"
                      placeholder="Ex: 10 ou 5.5"
                      data-testid="quantity-input"
                    />
                  </div>
                )}

                {/* Revenue Preview */}
                {selectedProduct && quantity && (
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-5 w-5 text-green-600" />
                        <span className="text-sm font-medium text-green-800">
                          Chiffre d'affaires
                        </span>
                      </div>
                      <span className="text-lg font-bold text-green-600" data-testid="revenue-preview">
                        {calculateRevenue().toLocaleString()} F CFA
                      </span>
                    </div>
                    
                    {/* Option de génération directe de reçu */}
                    <div className="mt-3 pt-3 border-t border-green-200">
                      <Button
                        type="button"
                        onClick={() => {
                          const productSold = getProductDetails(selectedProduct);
                          const saleData = {
                            products: [{
                              id: selectedProduct,
                              name: productSold?.name || 'Produit',
                              price: productSold?.price || 0,
                              quantity: parseFloat(quantity)
                            }]
                          };
                          setLastSaleData(saleData);
                          setShowInvoiceGenerator(true);
                        }}
                        variant="outline"
                        size="sm"
                        className="w-full border-green-300 text-green-700 hover:bg-green-100"
                      >
                        <Receipt className="h-4 w-4 mr-2" />
                        📄 Générer Reçu pour cette Vente
                      </Button>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={loading || !selectedProduct || !quantity}
                  className="w-full h-12 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-medium rounded-lg shadow-lg"
                  data-testid="submit-sale-btn"
                >
                  {loading ? (
                    <>
                      <div className="spinner mr-2"></div>
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Enregistrer la Vente
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Recent Sales */}
          <Card className="bg-white/70 glass border-0 shadow-lg slide-up">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-green-600" />
                Ventes Récentes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {Array.isArray(recentEntries) && recentEntries.length > 0 ? (
                <div className="space-y-3">
                  {recentEntries.map((entry, index) => {
                    if (!entry || !entry.id) return null;
                    
                    const product = getProductDetails(entry.product_id);
                    const counter = counters.find(c => c.id === product?.counter);
                    const revenue = product ? product.price * (entry.quantity_sold || 0) : 0;
                    
                    return (
                      <div key={`sales-entry-${entry.id}-${index}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className={`p-2 rounded-full ${counter?.color || 'bg-gray-100'}`}>
                            {counter?.id === 'pain' && (
                              <img src="https://customer-assets.emergentagent.com/job_baker-point-of-sale/artifacts/32b5mga4_image.png" alt="Pains" className="h-4 w-4 object-cover rounded" />
                            )}
                            {counter?.id === 'viennoiserie' && (
                              <img src="https://customer-assets.emergentagent.com/job_baker-point-of-sale/artifacts/d7eif7ef_image.png" alt="Viennoiseries" className="h-4 w-4 object-cover rounded" />
                            )}
                            {counter?.id === 'gateaux' && (
                              <Cake className="h-4 w-4" />
                            )}
                            {!counter && <ShoppingCart className="h-4 w-4" />}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {product?.name || 'Produit inconnu'}
                            </p>
                            <p className="text-sm text-gray-600">
                              {formatTime(entry.time)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex flex-col items-end gap-1">
                            <Badge variant="secondary" className="bg-green-100 text-green-800">
                              {entry.quantity_sold || 0} vendus
                            </Badge>
                            <span className="text-sm font-semibold text-green-600">
                              {product ? 
                                `${(parseFloat(entry.quantity_sold || 0) * product.price).toLocaleString('fr-FR')} F CFA`
                                : '0 F CFA'
                              }
                            </span>
                            {canDelete() && (
                              <button
                                onClick={() => handleDelete(entry.id)}
                                className="p-1 hover:bg-red-100 rounded transition-colors mt-1"
                                title="Supprimer"
                                data-testid={`delete-sale-${entry.id}`}
                              >
                                <Trash2 className="h-3 w-3 text-gray-400 hover:text-red-600" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }).filter(Boolean)}
                </div>
              ) : (
                <div className="text-center py-8">
                  <ShoppingCart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Aucune vente enregistrée aujourd'hui</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Générateur de Reçu/Facture */}
      <InvoiceGeneratorFixed 
        isOpen={showInvoiceGenerator}
        onClose={() => setShowInvoiceGenerator(false)}
        saleData={lastSaleData}
      />
      
      <Footer />
    </div>
  );
};

export default SalesEntry;